import { EventEmitter, Injectable } from '@angular/core';
import { of, Subject } from 'rxjs';
import { AppState } from 'core/common-entities/app-state.class';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private internalState: AppState;
  private goOfflineDateKey = 'goOfflineDate';
  goOnlineTriggeredKey = 'goOnlineTriggered';
  shouldSyncKey = 'shouldSync';
  offlineStateToggle$ = new Subject<boolean>();
  offlineTimerSyncTriggered$ = new Subject<any>();
  triggerStartGoOnlineSync$ = new Subject<boolean>();
  receiveOnlineUpdatesInProgress = false;
  receiveOnlineUpdatesDone$ = new EventEmitter<boolean>();
  triggerIsLoading$ = new Subject<boolean>();
  failedOnlineSyncStatusAlreadyHandled = false;
  failedLocalServer = false;

  constructor() {
    this.internalState = new AppState();
    this.internalState.isOnline = this.checkIfOnline();
    this.internalState.isElectron = navigator.userAgent.toLowerCase().indexOf('electron/') !== -1;
    this.internalState.isInternetConnected = navigator.onLine;
    this.internalState.localServerFailure = false;
    this.checkIfInternetConnection();
  }

  /**
   * Gets an immutable instance of the state
   */
  get state(): AppState {
    return Object.assign({}, this.internalState);
  }

  /**
   * Checks if the app is used in online mode
   * @returns as {boolean}
   */
  private checkIfOnline() {
    return !Boolean(window.localStorage.getItem(this.goOfflineDateKey));
  }

  /**
   * Checks if the device has internet connection
   */
  private checkIfInternetConnection() {
    window.addEventListener('online', () => this.internalState.isInternetConnected = true, false);
    window.addEventListener('offline', () => this.internalState.isInternetConnected = false, false);
  }

  /**
   * Sets the app in online state
   */
  goOnline() {
    this.internalState.isOnline = true;
    window.localStorage.removeItem(this.goOfflineDateKey);
    window.localStorage.setItem(this.shouldSyncKey, 'true');
    window.localStorage.setItem(this.goOnlineTriggeredKey, 'true');
    this.triggerIsLoading$.next(true);

    this.offlineStateToggle$.next(true);
    return of({});
  }

  /**
   * Sets the app in offline state
   */
  goOffline() {
    this.internalState.isOnline = false;
    this.offlineStateToggle$.next(false);
  }

  /**
   * Calculates the difference in days by the date when the user went offline and current date
   * @returns the rounded number of days of the app used in offline mode
   */
  getDifferenceBetweenGoOfflineAndToday() {
    const goOfflineDate = new Date(this.state.lastSynchronizationDate);
    const currentDate = new Date();
    // Use UTC midnight for both dates based on local calendar days to avoid DST skew
    const goOfflineMidnight = Date.UTC(goOfflineDate.getFullYear(), goOfflineDate.getMonth(), goOfflineDate.getDate());
    const currentMidnight = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

    return Math.round((currentMidnight - goOfflineMidnight) / (1000 * 3600 * 24));
  }

  /**
   * Sets the result from offline sync with the server
   */
  setOfflineSyncStatus(syncResult) {
    this.internalState.lastSynchronizationDate = syncResult.lastSynchronizationDate;
    this.internalState.synchronizationStatus = syncResult.synchronizationStatus;
    window.localStorage.setItem(this.goOfflineDateKey, syncResult.lastSynchronizationDate);
    this.offlineTimerSyncTriggered$.next(true); // notify that offline timer sync result has been changed
  }

  /**
   * Checks if the app is used in Electron mode and is out of sync
   * @returns - true if the app is out of sync, false if not
   */
  checkIfElectronAppIsOutOfSync() {
    return this.internalState.isElectron && this.internalState.synchronizationStatus === 3;
  }

  /**
   * Sets the flag when the local server fails to run
   */
  setLocalServerFailure() {
    this.internalState.localServerFailure = true;
  }
}
